import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useCallback, useState } from 'react';
import { fetchHalachicRange } from '../store/actions/halachicEngineActions';

/**
 * Hook for consuming multi-day halachic data (lookahead).
 * Auto-fetches on mount and auto-refreshes at sunset + midnight.
 *
 * @param {object} config - engine config (location, isIsrael, edah, etc.)
 * @param {number} [daysCount=7] - number of days to compute
 * @param {object} [options] - { autoFetch: true }
 * @returns {{ range, loading, error, lastUpdated, refresh }}
 */
export function useHalachicRange(config, daysCount = 7, options = { autoFetch: true }) {
  const dispatch = useDispatch();
  const { range, rangeLoading: loading, rangeError: error, rangeLastUpdated: lastUpdated } =
    useSelector((state) => state.halachicEngine);
  const [refreshTick, setRefreshTick] = useState(0);

  // Fetch on mount + re-fetch on config/daysCount change or refreshTick
  useEffect(() => {
    if (options.autoFetch && config?.location?.lat && config?.location?.lng && daysCount > 0) {
      dispatch(fetchHalachicRange(config, daysCount));
    }
  }, [
    config?.location?.lat,
    config?.location?.lng,
    config?.isIsrael,
    config?.edah,
    daysCount,
    refreshTick,
  ]);

  // Auto-refresh at sunset + midnight (same pattern as useHalachicData)
  useEffect(() => {
    const now = Date.now();
    const timers = [];

    // Use first day's sunset for refresh scheduling
    const sunset = range?.[0]?.zmanim?.sunset;
    if (sunset) {
      const [h, m] = sunset.split(':').map(Number);
      const sunsetDt = new Date();
      sunsetDt.setHours(h, m, 0, 0);
      const sunsetMs = sunsetDt.getTime();
      const targetMs = sunsetMs > now ? sunsetMs : sunsetMs + 86400000;
      if (targetMs > now) {
        timers.push(setTimeout(() => setRefreshTick(t => t + 1), targetMs - now + 63000));
      }
    }

    // Midnight refresh
    const nextMidnight = new Date();
    nextMidnight.setDate(nextMidnight.getDate() + 1);
    nextMidnight.setHours(0, 0, 15, 0);
    timers.push(setTimeout(() => setRefreshTick(t => t + 1), nextMidnight - now));

    return () => timers.forEach(clearTimeout);
  }, [range?.[0]?.zmanim?.sunset, range?.[0]?.date]);

  // Guard: detect missed sunset/midnight refreshes (sleep/wake, throttling)
  useEffect(() => {
    const day0 = range?.[0];
    const guard = setInterval(() => {
      const now = new Date();

      // After sunset but data still pre-sunset → missed refresh
      if (!day0?.isAfterSunset && day0?.zmanim?.sunset && day0.zmanim.sunset !== '--:--') {
        const [h, m] = day0.zmanim.sunset.split(':').map(Number);
        const sunset = new Date();
        sunset.setHours(h, m + 1, 3, 0);
        if (now > sunset) {
          setRefreshTick(t => t + 1);
          return;
        }
      }

      // Past midnight but data from yesterday
      if (day0?.date && !day0?.isAfterSunset) {
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        if (`${yyyy}-${mm}-${dd}` !== day0.date) {
          setRefreshTick(t => t + 1);
        }
      }
    }, 60_000);

    return () => clearInterval(guard);
  }, [range?.[0]?.isAfterSunset, range?.[0]?.zmanim?.sunset, range?.[0]?.date]);

  const refresh = useCallback(() => {
    if (config?.location?.lat && config?.location?.lng) {
      dispatch(fetchHalachicRange(config, daysCount));
    }
  }, [dispatch, config, daysCount]);

  return { range, loading, error, lastUpdated, refresh };
}
